import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  PlusIcon, 
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  ArrowsUpDownIcon,
  SwatchIcon,
  ArrowRightIcon,
  LinkIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { leadsApi } from '../config/api';

const LeadStatusManager = ({ isOpen, onClose, onStatusesUpdated }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState({ 
    name: '', 
    label: '',
    color: 'blue', 
    description: '',
    canTransitionTo: [],
    isFinal: false,
    outcome: ''
  });
  const [editingStatus, setEditingStatus] = useState(null);
  const [showFlowView, setShowFlowView] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);

  // ── Drag-and-drop state ──────────────────────────────
  const [dragIndex, setDragIndex] = useState(null);      // index being dragged
  const [dragOverIndex, setDragOverIndex] = useState(null); // index being hovered
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // allow drop
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const reordered = [...statuses];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setStatuses(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
    setOrderChanged(true);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      // Send updated order to backend — each status gets its new sequence index
      await Promise.all(
        statuses.map((status, index) =>
          leadsApi.put(`/lead-statuses/${status._id}`, { order: index })
        )
      );
      setOrderChanged(false);
      alert('Order saved successfully!');
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order. Please try again.');
    } finally {
      setSavingOrder(false);
    }
  };
  // ────────────────────────────────────────────────────

  const colorOptions = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-500', description: 'New/Initial' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-500', description: 'In Progress' },
    { value: 'green', label: 'Green', class: 'bg-green-500', description: 'Success' },
    { value: 'emerald', label: 'Emerald', class: 'bg-emerald-500', description: 'Qualified' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500', description: 'Proposal' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500', description: 'Negotiation' },
    { value: 'red', label: 'Red', class: 'bg-red-500', description: 'Lost/Negative' },
    { value: 'amber', label: 'Amber', class: 'bg-amber-500', description: 'Warning' },
    { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500', description: 'Scheduled' },
    { value: 'gray', label: 'Gray', class: 'bg-gray-500', description: 'On Hold' },
    { value: 'slate', label: 'Slate', class: 'bg-slate-500', description: 'Inactive' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
    }
  }, [isOpen]);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) {
        setStatuses(response.data.statuses);
      }
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStatus = async () => {
    if (!newStatus.name.trim() || !newStatus.label.trim()) {
      alert('Name and label are required');
      return;
    }

    try {
      console.log('🔄 Adding new status:', newStatus);
      const response = await leadsApi.post('/lead-statuses', newStatus);
      console.log('📥 Add status response:', response.data);
      
      if (response.data.success) {
        // Refresh the entire list to ensure we have the latest data
        await fetchStatuses();
        setNewStatus({ name: '', label: '', color: 'blue', description: '', canTransitionTo: [], isFinal: false, outcome: '' });
        alert('Status added successfully!');
      }
    } catch (error) {
      console.error('❌ Error adding status:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Error adding status';
      
      // If status already exists, refresh the list
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
        alert(`Status "${newStatus.name}" already exists in the database. Refreshing list...`);
        await fetchStatuses();
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleUpdateFlow = async (statusId, canTransitionTo) => {
    try {
      const response = await leadsApi.put(`/lead-statuses/${statusId}`, { canTransitionTo });
      if (response.data.success) {
        setStatuses(statuses.map(status => 
          status._id === statusId ? { ...status, canTransitionTo } : status
        ));
        setEditingFlow(null);
      }
    } catch (error) {
      console.error('Error updating status flow:', error);
      alert(error.response?.data?.message || 'Error updating status flow');
    }
  };

  const handleUpdateStatus = async (statusId, updateData) => {
    try {
      const response = await leadsApi.put(`/lead-statuses/${statusId}`, updateData);
      if (response.data.success) {
        setStatuses(statuses.map(status => 
          status._id === statusId ? response.data.status : status
        ));
        setEditingStatus(null);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleDeleteStatus = async (statusId) => {
    const statusToDelete = statuses.find(s => s._id === statusId);
    
    if (!window.confirm(`Are you sure you want to delete "${statusToDelete?.label}" status? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting status:', statusId);
      const response = await leadsApi.delete(`/lead-statuses/${statusId}`);
      console.log('📥 Delete response:', response.data);
      
      if (response.data.success) {
        // Refresh the entire list to ensure consistency
        await fetchStatuses();
        alert('Status deleted successfully!');
      }
    } catch (error) {
      console.error('❌ Error deleting status:', error);
      console.error('Error details:', error.response?.data);
      alert(error.response?.data?.message || 'Error deleting status. It may be in use by existing leads.');
    }
  };

  const handleSave = () => {
    onStatusesUpdated?.(statuses);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors mr-4"
            >
              ← Back to Dashboard
            </button>
            <div className="bg-blue-100 p-2 rounded-lg mr-3">
              <SwatchIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Lead Status Management</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFlowView(!showFlowView)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showFlowView 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'bg-gray-100 text-gray-700 border border-gray-200'
              }`}
            >
              <LinkIcon className="h-4 w-4 inline mr-2" />
              {showFlowView ? 'Hide Flow' : 'Show Flow'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="p-6">
            {/* Add New Status Form */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                Add New Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status Name</label>
                  <input
                    type="text"
                    placeholder="e.g., contacted"
                    value={newStatus.name}
                    onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Label</label>
                  <input
                    type="text"
                    placeholder="e.g., Contacted"
                    value={newStatus.label}
                    onChange={(e) => setNewStatus({ ...newStatus, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <select
                    value={newStatus.color}
                    onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {colorOptions.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.label} - {color.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    placeholder="Brief description"
                    value={newStatus.description}
                    onChange={(e) => setNewStatus({ ...newStatus, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Outcome Type</label>
                  <select
                    value={newStatus.outcome}
                    onChange={(e) => setNewStatus({ ...newStatus, outcome: e.target.value, isFinal: e.target.value !== '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None (In Progress)</option>
                    <option value="converted">✅ Successful/Won (Converted)</option>
                    <option value="rejected">❌ Rejected/Lost</option>
                    <option value="on_hold">⏸️ On Hold</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {newStatus.outcome === 'converted' && '✅ This status will count as WON in analytics'}
                    {newStatus.outcome === 'rejected' && '❌ This status will count as LOST in analytics'}
                    {newStatus.outcome === 'on_hold' && '⏸️ This status will be tracked separately'}
                    {newStatus.outcome === '' && 'ℹ️ This status is in-progress (not final)'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Initial Transitions (Optional)</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                    {statuses.map((status) => (
                      <label key={status._id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newStatus.canTransitionTo.includes(status.name)}
                          onChange={(e) => {
                            const newTransitions = e.target.checked
                              ? [...newStatus.canTransitionTo, status.name]
                              : newStatus.canTransitionTo.filter(name => name !== status.name);
                            setNewStatus({ ...newStatus, canTransitionTo: newTransitions });
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className={`w-3 h-3 rounded-full bg-${status.color}-500`}></div>
                        <span className="text-sm text-gray-700">{status.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <button
                    onClick={handleAddStatus}
                    disabled={!newStatus.name.trim() || !newStatus.label.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Status
                  </button>
                </div>
              </div>
            </div>

            {/* Status Flow Visualization */}
            {showFlowView && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                  <LinkIcon className="h-5 w-5 mr-2" />
                  Status Flow Visualization
                </h3>
                {statuses.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No statuses found. Please add some statuses first.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {statuses.map((status) => (
                      <div key={status._id} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded-full bg-${status.color}-500 mr-3`}></div>
                          <span className="font-medium text-gray-900">{status.label}</span>
                        </div>
                        <button
                          onClick={() => setEditingFlow(editingFlow === status._id ? null : status._id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {editingFlow === status._id ? 'Cancel' : 'Edit Flow'}
                        </button>
                      </div>
                      
                      {editingFlow === status._id ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">Select which statuses this can transition to:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {statuses
                              .filter(s => s._id !== status._id)
                              .map((targetStatus) => (
                                <label key={targetStatus._id} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={status.canTransitionTo?.includes(targetStatus.name) || false}
                                    onChange={(e) => {
                                      const currentTransitions = status.canTransitionTo || [];
                                      const newTransitions = e.target.checked
                                        ? [...currentTransitions, targetStatus.name]
                                        : currentTransitions.filter(name => name !== targetStatus.name);
                                      
                                      handleUpdateFlow(status._id, newTransitions);
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className={`w-3 h-3 rounded-full bg-${targetStatus.color}-500`}></div>
                                  <span className="text-sm text-gray-700">{targetStatus.label}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Can transition to:</span>
                          {status.canTransitionTo && status.canTransitionTo.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {status.canTransitionTo.map((transitionName) => {
                                const targetStatus = statuses.find(s => s.name === transitionName);
                                return targetStatus ? (
                                  <div key={transitionName} className="flex items-center space-x-1 bg-gray-100 px-2 py-1 rounded-full">
                                    <div className={`w-2 h-2 rounded-full bg-${targetStatus.color}-500`}></div>
                                    <span className="text-xs text-gray-700">{targetStatus.label}</span>
                                    <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                                  </div>
                                ) : null;
                              })}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">No transitions defined</span>
                          )}
                        </div>
                      )}
                    </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Status List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <ArrowsUpDownIcon className="h-5 w-5 mr-2" />
                  Current Statuses
                  <span className="ml-2 text-xs text-gray-400 font-normal">(drag ⠿ to reorder)</span>
                </h3>
                {orderChanged && (
                  <button
                    onClick={handleSaveOrder}
                    disabled={savingOrder}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {savingOrder ? (
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <CheckIcon className="h-3.5 w-3.5" />
                    )}
                    Save Order
                  </button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading statuses...</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {statuses.map((status, index) => (
                    <div
                      key={status._id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white border-2 rounded-lg p-4 transition-all duration-150 ${
                        dragIndex === index
                          ? 'opacity-40 scale-95 border-dashed border-gray-400'
                          : dragOverIndex === index
                          ? 'border-indigo-400 shadow-lg bg-indigo-50'
                          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Drag handle + status info */}
                        <div className="flex items-center space-x-3">
                          {/* Drag handle */}
                          <div
                            className="flex flex-col gap-0.5 px-1 py-1 rounded hover:bg-gray-100 transition-colors"
                            title="Drag to reorder"
                            style={{ cursor: 'grab' }}
                          >
                            <span className="text-gray-400 text-lg leading-none select-none" style={{ letterSpacing: '-2px' }}>⠿</span>
                          </div>

                          {/* Order badge */}
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </span>

                          <div className={`w-4 h-4 rounded-full flex-shrink-0 bg-${status.color}-500`}></div>

                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900">{status.label}</h4>
                              {status.outcome === 'converted' && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">WON</span>
                              )}
                              {status.outcome === 'rejected' && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">LOST</span>
                              )}
                              {status.outcome === 'on_hold' && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">HOLD</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">Name: {status.name}</p>
                            {status.description && (
                              <p className="text-sm text-gray-600">{status.description}</p>
                            )}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button
                            onClick={() => setEditingStatus(status)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteStatus(status._id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 flex items-center"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Edit Status Modal */}
      {editingStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Status</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status Name</label>
                  <input
                    type="text"
                    value={editingStatus.name}
                    onChange={(e) => setEditingStatus({ ...editingStatus, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Label</label>
                  <input
                    type="text"
                    value={editingStatus.label}
                    onChange={(e) => setEditingStatus({ ...editingStatus, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <select
                    value={editingStatus.color}
                    onChange={(e) => setEditingStatus({ ...editingStatus, color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {colorOptions.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.label} - {color.description}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={editingStatus.description || ''}
                    onChange={(e) => setEditingStatus({ ...editingStatus, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Can Transition To</label>
                  <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {statuses
                      .filter(s => s._id !== editingStatus._id)
                      .map((status) => (
                        <label key={status._id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editingStatus.canTransitionTo?.includes(status.name) || false}
                            onChange={(e) => {
                              const currentTransitions = editingStatus.canTransitionTo || [];
                              const newTransitions = e.target.checked
                                ? [...currentTransitions, status.name]
                                : currentTransitions.filter(name => name !== status.name);
                              setEditingStatus({ ...editingStatus, canTransitionTo: newTransitions });
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className={`w-3 h-3 rounded-full bg-${status.color}-500`}></div>
                          <span className="text-sm text-gray-700">{status.label}</span>
                        </label>
                      ))}
                    {statuses.filter(s => s._id !== editingStatus._id).length === 0 && (
                      <p className="text-sm text-gray-500 italic">No other statuses available</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingStatus(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateStatus(editingStatus._id, editingStatus)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadStatusManager;
